import { useRef, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { UploadSimple, X, CircleNotch, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * ImageUploader — file picker + preview + upload to /api/uploads/image
 * Props:
 *  - value: current image URL (external or /api/files/... path)
 *  - onChange(url): called with the new absolute URL
 *  - testid: base test id
 *  - label: field label
 */
export default function ImageUploader({ value, onChange, testid = "image-upload", label = "Photo" }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // data.url is /api/files/... — turn into absolute URL that <img src> can use
      const absolute = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange(absolute);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="text-xs font-semibold text-[#5C564D] mb-1">{label}</div>
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative">
            <img src={value} alt="Preview" className="w-20 h-20 rounded-xl object-cover border border-[#E5D9CC]" data-testid={`${testid}-preview`} />
            <button
              type="button"
              onClick={() => onChange("")}
              data-testid={`${testid}-remove`}
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#1C1B1A] text-[#F9F6F0] flex items-center justify-center hover:bg-[#D95A41]"
              style={{ transition: "background-color 180ms ease-out" }}
              aria-label="Remove image"
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        ) : (
          <div className="w-20 h-20 rounded-xl border border-dashed border-[#E5D9CC] bg-[#F2EBE5] flex items-center justify-center">
            <Camera size={24} className="text-[#5C564D]" />
          </div>
        )}
        <div className="flex-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`${testid}-input`}
            onChange={(e) => upload(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid={`${testid}-btn`}
            className="samaaj-btn-outline text-sm flex items-center gap-2"
          >
            {busy ? <><CircleNotch size={14} className="animate-spin" /> Uploading…</> : <><UploadSimple size={14} /> {value ? "Replace photo" : "Upload photo"}</>}
          </button>
          <div className="text-xs text-[#5C564D] mt-1">JPG, PNG or WEBP up to 5MB.</div>
        </div>
      </div>
    </div>
  );
}
